import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { searchProcessos, type ProcessoInterno } from '@/api/processos';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { classifyError } from '@/lib/errors';
import { formatNumeroProcesso } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

const CNJ_FORMATTED_REGEX = /^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/;
const CNJ_PREFIX = 'cnj-lookup:';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  confirmIcon?: ComponentType<{ className?: string }>;
  /** Chamado com o número CNJ do processo escolhido ao confirmar. */
  onConfirm: (numero: string) => void;
}

/**
 * Normaliza o que o usuário digitou para o formato CNJ canônico, quando
 * possível. Aceita o número já formatado ou 20 dígitos contínuos. Retorna
 * `null` se não parecer um CNJ válido — nesse caso o dialog se comporta como
 * busca textual normal.
 */
function tryParseCnj(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (CNJ_FORMATTED_REGEX.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 20) return formatNumeroProcesso(digits);
  return null;
}

/**
 * Diálogo genérico de seleção de processo: busca server-side com debounce,
 * opção de consultar o tribunal para um CNJ ainda não cadastrado e confirmação
 * que devolve o número escolhido via `onConfirm`. Encapsula toda a lógica de
 * UI para ser reaproveitado por "Nova petição" e "Usar modelo" (antes
 * duplicada nos dois dialogs).
 *
 * O número do processo é capturado NO MOMENTO da seleção (`selectedNumero`),
 * não re-derivado da lista no confirmar — assim a confirmação nunca falha em
 * silêncio caso a busca tenha sido refeita e a opção saído da lista.
 */
export function ProcessPickerDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmIcon: ConfirmIcon,
  onConfirm,
}: Props) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedNumero, setSelectedNumero] = useState<string | null>(null);

  // Reseta a seleção e fecha o dialog. Centraliza o reset (cancelar, X,
  // clique fora e confirmação) sem usar effect — evita renders em cascata.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSearch('');
      setDebouncedSearch('');
      setSelected(null);
      setSelectedNumero(null);
    }
    onOpenChange(next);
  };

  // Debounce simples para a busca server-side.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const {
    data: processos = [],
    isLoading,
    isFetching,
    error,
  } = useQuery<ProcessoInterno[]>({
    queryKey: [...queryKeys.processos.all, 'search', debouncedSearch || null],
    queryFn: () => searchProcessos(debouncedSearch || undefined),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const cnjDigitado = tryParseCnj(search);
  const matchExatoNaLista = cnjDigitado
    ? processos.some((p) => p.numero === cnjDigitado)
    : false;
  const mostrarOpcaoLookup = !!cnjDigitado && !matchExatoNaLista;

  // Limita a 50 resultados para não sobrecarregar o Combobox; a busca já é
  // server-side mas pode trazer uma página inteira em buscas curtas.
  const options = useMemo(() => {
    const list: Array<{
      value: string;
      label: string;
      keywords?: string[];
      data?: ProcessoInterno;
      isLookup?: boolean;
    }> = processos.slice(0, 50).map((p) => ({
      value: p.id,
      label: p.numero,
      keywords: [p.numero, p.tribunal ?? ''],
      data: p,
    }));
    if (mostrarOpcaoLookup && cnjDigitado) {
      list.unshift({
        value: `${CNJ_PREFIX}${cnjDigitado}`,
        label: cnjDigitado,
        isLookup: true,
      });
    }
    return list;
  }, [processos, mostrarOpcaoLookup, cnjDigitado]);

  // Captura o número no momento da seleção (não re-deriva no confirmar).
  const handleSelect = (val: string) => {
    if (!val) {
      setSelected(null);
      setSelectedNumero(null);
      return;
    }
    setSelected(val);
    if (val.startsWith(CNJ_PREFIX)) {
      setSelectedNumero(val.slice(CNJ_PREFIX.length));
      return;
    }
    const proc = processos.find((p) => p.id === val);
    setSelectedNumero(proc ? proc.numero : null);
  };

  const handleConfirm = () => {
    if (!selectedNumero) return;
    const numero = selectedNumero;
    handleOpenChange(false);
    onConfirm(numero);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Combobox
          options={options}
          value={selected ?? ''}
          onValueChange={handleSelect}
          placeholder="Selecione ou digite o número do processo"
          searchPlaceholder="Buscar por número ou tribunal..."
          hasError={!!error}
          emptyMessage={
            isLoading || isFetching
              ? 'Buscando processos...'
              : 'Nenhum processo na sua lista. Digite o número CNJ para consultar o tribunal.'
          }
          shouldFilter={false}
          searchValue={search}
          onSearchChange={setSearch}
          loading={isLoading}
          renderOption={(opt) => {
            const lookupOpt = opt as typeof opt & { isLookup?: boolean };
            if (lookupOpt.isLookup) {
              return (
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate font-mono text-sm">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">consultar tribunal</span>
                </div>
              );
            }
            const p = (opt as typeof opt & { data?: ProcessoInterno }).data;
            if (!p) return opt.label;
            return (
              <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <span className="truncate font-mono text-sm">{p.numero}</span>
                {p.tribunal && <Badge variant="outline">{p.tribunal}</Badge>}
              </div>
            );
          }}
        />
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{classifyError(error).message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedNumero}>
            {ConfirmIcon && <ConfirmIcon className="mr-2 h-4 w-4" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
